import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { notify } from "@/lib/notify";

import { authApi, type LoginResult } from "@/lib/api/auth";
import { ApiRequestError } from "@/lib/api/client";
import { useAuthStore } from "@/stores/auth-store";
import { Icon } from "@/components/layout/Icon";
import { Spinner } from "@/components/ui/Spinner";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { useMountTransition } from "@/lib/utils/useMountTransition";
import { cn } from "@/lib/utils/cn";
import { useBranding, type ResolvedBranding } from "@/lib/branding";
import { darkenForWhiteText, hexToRgb, rgbToHex } from "@/lib/branding/theme";
import { MFASetupPanel } from "./MFASetupPanel";

const schema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
  tenant_slug: z.string().optional(),
  mfa_code: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

// Dark, translucent field to match the glass card - deliberately not the
// shared Input/PasswordInput components. Those hard-code a light theme
// (white background, slate border) via string-concatenated classes with no
// tailwind-merge in `cn()` (see lib/utils/cn.ts), so a caller-supplied
// className can't reliably override them - the winner depends on Tailwind's
// generated stylesheet order, not JSX order. Reimplementing the couple of
// fields this page needs avoids that footgun entirely and keeps every other
// screen that uses Input/PasswordInput untouched.
const fieldClass =
  "h-11 w-full rounded-lg border border-white/15 bg-white/[0.06] px-3.5 text-[15px] text-[#f3ede0] " +
  "placeholder:text-slate-400/50 transition-colors duration-200 ease-out hover:border-white/25 " +
  "focus:border-gold-400 focus:outline-none focus:ring-1 focus:ring-gold-400/50 disabled:opacity-50";
const fieldErrorClass = "border-red-400/70 focus:border-red-400 focus:ring-red-400/40";
const labelClass = "mb-1.5 block text-sm font-medium text-slate-200/90";
const hintClass = "mt-1.5 text-xs text-slate-400/70";
const errorTextClass = "mt-1.5 text-xs text-red-300";

export function LoginPage() {
  const branding = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const [needsWorkspace, setNeedsWorkspace] = useState(false);
  const [needsMFACode, setNeedsMFACode] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  // Set only when a login attempt reports mfa_setup_required: true - the
  // account's MFA is required but not yet enrolled. access_token here is a
  // short-lived MFA-setup token (security.TokenMFASetup on the backend),
  // never treated as a real session (setSession is never called for it).
  const [mfaSetup, setMFASetup] = useState<LoginResult | null>(null);

  // Hides "Forgot password?" when a Super Admin has turned the
  // password_reset feature toggle off for this tenant. Any other failure
  // (offline, a slow backend) leaves the link showing - failing open here
  // is a UX call, not a security one: the endpoint itself still enforces
  // the toggle regardless of what this link renders.
  const passwordResetAvailability = useQuery({
    queryKey: ["auth", "password-reset-availability"],
    queryFn: () => authApi.passwordResetAvailability(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const hidePasswordResetLink =
    passwordResetAvailability.error instanceof ApiRequestError &&
    passwordResetAvailability.error.appError.code === "FEATURE_DISABLED";

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const mutation = useMutation({
    // This form already shows its own persistent inline ErrorMessage banner
    // (below) instead of a transient toast - a login failure is exactly the
    // "handles its own failure UI" case the global handler's opt-out exists
    // for, so this mutation skips the automatic error toast to avoid saying
    // the same thing twice.
    meta: { silent: true },
    mutationFn: authApi.login,
    onSuccess: (result) => {
      if (result.mfa_setup_required) {
        setMFASetup(result);
        return;
      }
      setSession({
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        user: result.user,
        tenant: result.tenant,
      });
      notify.success(`Welcome back, ${result.user.first_name}!`);
      const from = (location.state as { from?: Location })?.from?.pathname ?? "/";
      navigate(from, { replace: true });
    },
    onError: (error) => {
      if (error instanceof ApiRequestError) {
        if (error.appError.code === "INVALID_INPUT" && error.appError.fields?.some((f) => f.field === "tenant_slug")) {
          setNeedsWorkspace(true);
          return;
        }
        if (error.appError.fields?.some((f) => f.field === "mfa_code")) {
          setNeedsMFACode(true);
          return;
        }
        for (const field of error.appError.fields ?? []) {
          if (field.field === "email" || field.field === "password" || field.field === "tenant_slug") {
            setError(field.field, { message: field.message });
          }
        }
      }
    },
  });

  // A correct sign-in from a device a Tenant Admin has not approved yet
  // (device approval feature) - shown as "waiting", not as a failure.
  const devicePending =
    mutation.error instanceof ApiRequestError && mutation.error.appError.code === "DEVICE_APPROVAL_PENDING";

  const { shouldRender: showSpinnerSlot, isVisible: spinnerVisible } = useMountTransition(mutation.isPending, 150);

  if (mfaSetup) {
    return (
      <div className="premium-login relative flex min-h-screen items-center justify-center overflow-hidden bg-navy-950 px-4 py-10">
        <AmbientGlow />
        <div className="relative w-full max-w-sm animate-login-slide-up">
          <div className="rounded-2xl bg-white p-6 shadow-2xl">
            <MFASetupPanel
              setupToken={mfaSetup.access_token}
              onComplete={() => {
                notify.success("Multi-factor authentication enabled. Sign in again to continue.");
                setMFASetup(null);
                setNeedsMFACode(true);
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="premium-login relative flex min-h-screen overflow-hidden bg-navy-950"
      style={loginBackgroundStyle(branding)}
    >
      {branding.loginBackgroundImage && (
        // Scrim over a tenant's background photo so the light text stays readable.
        <div className="pointer-events-none absolute inset-0 bg-navy-950/55" aria-hidden="true" />
      )}
      {/* Decorative hero panel - desktop only. Mobile gets a plain dark
          background instead (see the AmbientGlow-only wrapper below the
          form), never this SVG ribbon scene, per the "simplify the
          decorative background on mobile" requirement. */}
      <div className="relative hidden w-[58%] flex-col justify-between overflow-hidden px-12 py-12 md:flex lg:w-3/5 lg:px-16 lg:py-14 xl:px-20">
        <AmbientGlow />
        <RibbonScene />

        <BrandMark branding={branding} className="relative z-10" />

        <div className="relative z-10 max-w-xl animate-login-fade-in">
          <h1 className="font-display text-4xl font-medium leading-[1.15] text-[#f7f1e1] lg:text-5xl xl:text-[3.35rem]">
            People first.
            <br />
            Possibilities ahead.
          </h1>
          <p className="mt-5 max-w-md text-base text-slate-300/80 lg:text-lg">
            Your workspace, beautifully connected.
          </p>
        </div>

        <p className="relative z-10 text-sm tracking-wide text-slate-300/70">
          People <span className="mx-2.5 text-gold-400/70">•</span> Attendance{" "}
          <span className="mx-2.5 text-gold-400/70">•</span> Payroll
        </p>
      </div>

      {/* Form panel */}
      <div className="relative flex w-full flex-1 items-center justify-center overflow-y-auto px-5 py-10 sm:px-8 md:w-[42%] lg:w-2/5">
        <div className="pointer-events-none absolute inset-0 md:hidden" aria-hidden="true">
          <div className="absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-blue-500/10 blur-[100px]" />
        </div>

        <div className="relative w-full max-w-sm">
          <div className="mb-8 flex justify-center md:hidden">
            <BrandMark branding={branding} />
          </div>

          <div className="animate-login-slide-up rounded-2xl border border-gold-400/25 bg-white/[0.045] p-7 shadow-2xl backdrop-blur-xl sm:p-8">
            <div className="mb-7 text-center md:text-left">
              <h2 className="font-display text-2xl font-medium text-[#f7f1e1]">
                {branding.loginTitle || "Welcome back"}
              </h2>
              <p className="mt-1.5 text-sm text-slate-300/70">{branding.loginSubtitle || "Sign in to your workspace"}</p>
            </div>

            <form
              onSubmit={handleSubmit(
                (values) => mutation.mutate(values),
                () => notify.warning("Please fill all required fields."),
              )}
              className="flex flex-col gap-4"
            >
              <fieldset disabled={mutation.isPending} className="contents">
                <div>
                  <label htmlFor="login-email" className={labelClass}>
                    Work email
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="username"
                    placeholder="you@company.com"
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? "login-email-error" : undefined}
                    className={cn(fieldClass, errors.email && fieldErrorClass)}
                    {...register("email")}
                  />
                  {errors.email && (
                    <p id="login-email-error" className={errorTextClass}>
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label htmlFor="login-password" className="text-sm font-medium text-slate-200/90">
                      Password
                    </label>
                    {!hidePasswordResetLink && (
                      <Link
                        to="/forgot-password"
                        className="rounded text-xs text-gold-300 underline-offset-2 hover:text-gold-200 hover:underline"
                      >
                        Forgot password?
                      </Link>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      id="login-password"
                      type={passwordVisible ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      aria-invalid={Boolean(errors.password)}
                      aria-describedby={errors.password ? "login-password-error" : undefined}
                      className={cn(fieldClass, "pr-10", errors.password && fieldErrorClass)}
                      {...register("password")}
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible((v) => !v)}
                      title={passwordVisible ? "Hide password" : "Show password"}
                      aria-label={passwordVisible ? "Hide password" : "Show password"}
                      aria-pressed={passwordVisible}
                      className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg text-slate-400 transition-colors duration-200 hover:text-gold-300"
                    >
                      <Icon name={passwordVisible ? "eye-off" : "eye"} className="h-4 w-4" />
                    </button>
                  </div>
                  {errors.password && (
                    <p id="login-password-error" className={errorTextClass}>
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {needsWorkspace && (
                  <div>
                    <label htmlFor="login-workspace" className={labelClass}>
                      Workspace
                    </label>
                    <input
                      id="login-workspace"
                      placeholder="acme-manufacturing"
                      aria-describedby="login-workspace-hint"
                      className={cn(fieldClass, errors.tenant_slug && fieldErrorClass)}
                      {...register("tenant_slug")}
                    />
                    {errors.tenant_slug ? (
                      <p className={errorTextClass}>{errors.tenant_slug.message}</p>
                    ) : (
                      <p id="login-workspace-hint" className={hintClass}>
                        This email is registered with more than one workspace. Enter its identifier from the URL
                        your admin shared.
                      </p>
                    )}
                  </div>
                )}

                {needsMFACode && (
                  <div>
                    <label htmlFor="login-mfa-code" className={labelClass}>
                      Authentication code
                    </label>
                    <input
                      id="login-mfa-code"
                      placeholder="123456"
                      autoComplete="one-time-code"
                      aria-describedby="login-mfa-hint"
                      className={cn(fieldClass, errors.mfa_code && fieldErrorClass)}
                      {...register("mfa_code")}
                    />
                    {errors.mfa_code ? (
                      <p className={errorTextClass}>{errors.mfa_code.message}</p>
                    ) : (
                      <p id="login-mfa-hint" className={hintClass}>
                        Enter the code from your authenticator app, or one of your backup codes.
                      </p>
                    )}
                  </div>
                )}

                {devicePending && (
                  <div
                    role="status"
                    data-testid="device-approval-pending"
                    className="rounded-lg border border-gold-400/40 bg-gold-400/10 px-4 py-3 text-sm text-gold-100"
                  >
                    <p className="font-semibold">Waiting for approval</p>
                    <p className="mt-1">
                      This is a new device for your account. Your administrator has been asked to approve it - sign in
                      again once they have.
                    </p>
                  </div>
                )}
                {mutation.isError && !needsWorkspace && !needsMFACode && !devicePending && (
                  <ErrorMessage
                    error={mutation.error}
                    className="rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200"
                  />
                )}
                {mutation.isError && needsMFACode && getValues("mfa_code") && (
                  <ErrorMessage
                    error={mutation.error}
                    className="rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200"
                  />
                )}

                <button
                  type="submit"
                  className={cn(
                    "mt-1 inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg text-sm font-semibold transition-all duration-200 ease-out hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]",
                    // A white-labeled workspace's primary colour drives the
                    // button (primary-700 is contrast-safe for white text).
                    branding.primaryColor
                      ? "bg-primary-700 text-white hover:bg-primary-800 active:bg-primary-900"
                      : "bg-gold-400 text-navy-950 hover:bg-gold-300 hover:shadow-[0_10px_24px_-8px_rgba(211,164,78,0.55)] active:bg-gold-500",
                    "disabled:pointer-events-none disabled:translate-y-0 disabled:opacity-50 disabled:shadow-none",
                  )}
                >
                  {showSpinnerSlot && (
                    <span
                      className={cn(
                        "grid shrink-0 overflow-hidden transition-all duration-150 ease-out",
                        spinnerVisible ? "w-3.5 opacity-100" : "w-0 opacity-0",
                      )}
                      aria-hidden="true"
                    >
                      <Spinner className="h-3.5 w-3.5" />
                    </span>
                  )}
                  Sign in
                  <Icon name="arrow-right" className="h-4 w-4" aria-hidden="true" />
                </button>
              </fieldset>
            </form>
          </div>

          <SupportLine branding={branding} />
        </div>
      </div>
    </div>
  );
}

function BrandMark({ branding, className }: { branding: ResolvedBranding; className?: string }) {
  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <img
        src={branding.logoSrc}
        alt={`${branding.companyName} logo`}
        className="h-14 w-14 shrink-0 rounded-lg object-contain"
      />
      <span className="text-lg font-semibold tracking-wide text-[#f3ede0]">{branding.companyName}</span>
    </div>
  );
}

/** Tenant support contact when white labeling sets one, else the stock hint. */
function SupportLine({ branding }: { branding: ResolvedBranding }) {
  const { supportEmail, supportPhone } = branding;
  if (!supportEmail && !supportPhone) {
    return (
      <p className="mt-6 text-center text-xs text-slate-400/70">
        Need access? <span className="text-slate-300">Contact your HR team</span>
      </p>
    );
  }
  return (
    <p className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-xs text-slate-400/80">
      <span>Need help?</span>
      {supportEmail && (
        <a href={`mailto:${supportEmail}`} className="text-slate-200 underline-offset-2 hover:underline">
          {supportEmail}
        </a>
      )}
      {supportPhone && (
        <a href={`tel:${supportPhone.replace(/[^+0-9]/g, "")}`} className="text-slate-200 underline-offset-2 hover:underline">
          {supportPhone}
        </a>
      )}
    </p>
  );
}

/**
 * The page background a white-labeled workspace chose. A colour is darkened
 * only as far as the page's light text needs to stay readable; a photo gets
 * a scrim (rendered in the page) for the same reason.
 */
function loginBackgroundStyle(branding: ResolvedBranding): React.CSSProperties | undefined {
  if (branding.loginBackgroundImage) {
    return {
      backgroundImage: `url("${encodeURI(branding.loginBackgroundImage)}")`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  }
  if (branding.loginBackgroundColor) {
    return { backgroundColor: rgbToHex(darkenForWhiteText(hexToRgb(branding.loginBackgroundColor), 7)) };
  }
  return undefined;
}

/** Two soft blurred blobs standing in for "ambient lighting" - static, not
 * animated, per the "avoid excessive glow" constraint; only the ribbons
 * themselves move. */
function AmbientGlow() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="absolute -top-32 left-1/4 h-[480px] w-[480px] rounded-full bg-blue-500/15 blur-[130px]" />
      <div className="absolute bottom-0 right-0 h-[420px] w-[420px] translate-x-1/4 translate-y-1/4 rounded-full bg-navy-500/25 blur-[120px]" />
    </div>
  );
}

/** Flowing blue ribbons with restrained gold accents, recreated as inline
 * SVG paths (no image asset, no animation library) per the implementation
 * constraints. Two layers drift independently on a transform-only CSS
 * animation (see index.css's ribbon-drift-a/b keyframes, 18s/22s loops) -
 * the global prefers-reduced-motion rule in index.css already collapses
 * every animation-duration to ~0 app-wide, so this needs no reduced-motion
 * handling of its own. */
function RibbonScene() {
  return (
    <svg
      viewBox="0 0 800 1000"
      preserveAspectRatio="xMidYMid slice"
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ribbon-blue-1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#345599" stopOpacity="0.05" />
          <stop offset="45%" stopColor="#5578c9" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#345599" stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="ribbon-blue-2" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#243f78" stopOpacity="0.04" />
          <stop offset="50%" stopColor="#4a6bb8" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#243f78" stopOpacity="0.04" />
        </linearGradient>
        <linearGradient id="ribbon-gold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#d3a44e" stopOpacity="0" />
          <stop offset="50%" stopColor="#e9cd8d" stopOpacity="0.65" />
          <stop offset="100%" stopColor="#d3a44e" stopOpacity="0" />
        </linearGradient>
      </defs>

      <g className="animate-ribbon-drift-a" style={{ transformOrigin: "20% 60%" }}>
        <path
          d="M-80 120C60 260 40 420 180 520C320 620 300 780 140 920"
          stroke="url(#ribbon-blue-1)"
          strokeWidth="70"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M-140 260C10 380 -10 520 130 620C260 710 250 850 100 980"
          stroke="url(#ribbon-blue-1)"
          strokeWidth="34"
          strokeLinecap="round"
          fill="none"
          opacity="0.7"
        />
      </g>

      <g className="animate-ribbon-drift-b" style={{ transformOrigin: "35% 40%" }}>
        <path
          d="M-40 40C140 150 120 320 260 430C400 540 380 700 220 860"
          stroke="url(#ribbon-blue-2)"
          strokeWidth="90"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M20 -20C190 90 170 260 300 360C420 460 410 610 270 760"
          stroke="url(#ribbon-gold)"
          strokeWidth="2.5"
          strokeLinecap="round"
          fill="none"
        />
      </g>

      <circle cx="238" cy="358" r="2.2" fill="#e9cd8d" opacity="0.8" />
      <circle cx="310" cy="520" r="1.6" fill="#e9cd8d" opacity="0.6" />
      <circle cx="120" cy="700" r="1.8" fill="#e9cd8d" opacity="0.5" />
    </svg>
  );
}
